'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConversionRates {
  label: string;
  code: string;
  symbol: string;
  rate: number;
}

const conversionRates: ConversionRates[] = [
  { label: 'Euro (EUR)', code: 'EUR', symbol: '€', rate: 1 },
  { label: 'Indian Rupee (INR)', code: 'INR', symbol: '₹', rate: 90.5 },
  { label: 'US Dollar (USD)', code: 'USD', symbol: '$', rate: 1.08 },
  { label: 'British Pound (GBP)', code: 'GBP', symbol: '£', rate: 0.86 },
  { label: 'UAE Dirham (AED)', code: 'AED', symbol: 'د.إ', rate: 3.97 },
  { label: 'Australian Dollar (AUD)', code: 'AUD', symbol: 'A$', rate: 1.65 },
  { label: 'Canadian Dollar (CAD)', code: 'CAD', symbol: 'C$', rate: 1.47 },
  { label: 'Singapore Dollar (SGD)', code: 'SGD', symbol: 'S$', rate: 1.45 },
];

const programTypes = [
  { label: 'Undergraduate (Bachelor\'s)', minTuition: 9850, maxTuition: 25000 },
  { label: 'Postgraduate (Master\'s)', minTuition: 10000, maxTuition: 35000 },
  { label: 'PhD / Research', minTuition: 6000, maxTuition: 18000 },
  { label: 'Language / Foundation', minTuition: 5000, maxTuition: 12000 },
];

const fieldOfStudy = [
  { label: 'Business & Management', multiplier: 1.1 },
  { label: 'Computer Science & IT', multiplier: 1.15 },
  { label: 'Engineering', multiplier: 1.2 },
  { label: 'Medicine & Health Sciences', multiplier: 1.5 },
  { label: 'Arts & Humanities', multiplier: 0.9 },
  { label: 'Law', multiplier: 1.25 },
  { label: 'Science & Research', multiplier: 1.0 },
];

const scholarships = [
  { name: 'Government of Ireland International Education Scholarship', amount: 10000, eligibility: 'Non-EU/EEA students with outstanding academic record' },
  { name: 'University Merit Scholarship', amount: 5000, eligibility: 'High GPA (3.5+ or equivalent)' },
  { name: 'STEM Excellence Award', amount: 7500, eligibility: 'Students in STEM programs' },
  { name: 'Sports Achievement Scholarship', amount: 3000, eligibility: 'National/international sports representation' },
  { name: 'Early Application Discount', amount: 2000, eligibility: 'Applications submitted 6+ months before intake' },
];

const accommodationOptions = [
  { label: 'University Dormitory (shared)', cost: 600 },
  { label: 'University Dormitory (single)', cost: 900 },
  { label: 'Private Shared Apartment', cost: 750 },
  { label: 'Private Studio Apartment', cost: 1200 },
  { label: 'Homestay', cost: 850 },
];

export default function IrelandCostCalculator() {
  const [selectedProgram, setSelectedProgram] = useState(0);
  const [selectedField, setSelectedField] = useState(0);
  const [tuitionAmount, setTuitionAmount] = useState(15000);
  const [selectedAccommodation, setSelectedAccommodation] = useState(0);
  const [foodBudget, setFoodBudget] = useState(350);
  const [transportBudget, setTransportBudget] = useState(120);
  const [entertainmentBudget, setEntertainmentBudget] = useState(200);
  const [selectedScholarships, setSelectedScholarships] = useState<number[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState(0);
  const [duration, setDuration] = useState(12);
  const [activeSection, setActiveSection] = useState<'tuition' | 'living' | 'scholarships' | 'summary'>('tuition');

  const currency = conversionRates[selectedCurrency];

  const convert = (eurAmount: number) => eurAmount * currency.rate;
  const fmt = (amount: number) => {
    const converted = convert(amount);
    return `${currency.symbol}${converted.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const program = programTypes[selectedProgram];
  const field = fieldOfStudy[selectedField];

  const adjustedTuition = Math.round(tuitionAmount * field.multiplier);
  const totalTuition = Math.round(adjustedTuition * (duration / 12));

  const monthlyLiving = accommodationOptions[selectedAccommodation].cost + foodBudget + transportBudget + entertainmentBudget + 80; // 80 for utilities
  const totalLiving = monthlyLiving * duration;

  const totalScholarshipAmount = selectedScholarships.reduce((sum, idx) => sum + scholarships[idx].amount, 0);

  const grossTotal = totalTuition + totalLiving;
  const netTotal = Math.max(0, grossTotal - totalScholarshipAmount);

  const toggleScholarship = (idx: number) => {
    setSelectedScholarships(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const sections = [
    { id: 'tuition', label: 'Tuition', icon: 'AcademicCapIcon' },
    { id: 'living', label: 'Living Costs', icon: 'HomeIcon' },
    { id: 'scholarships', label: 'Scholarships', icon: 'GiftIcon' },
    { id: 'summary', label: 'Summary', icon: 'CalculatorIcon' },
  ] as const;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">🇮🇪</span>
          <h2 className="text-2xl font-bold">Ireland Study Cost Calculator</h2>
        </div>
        <p className="text-emerald-100 text-sm">Estimate your total study expenses with EUR currency conversion</p>
        {/* Currency Selector */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-emerald-100 text-sm font-medium">Display in:</span>
          <select
            value={selectedCurrency}
            onChange={e => setSelectedCurrency(Number(e.target.value))}
            className="bg-white/20 text-white border border-white/30 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            {conversionRates.map((c, i) => (
              <option key={c.code} value={i} className="text-foreground bg-background">{c.label}</option>
            ))}
          </select>
          {selectedCurrency !== 0 && (
            <span className="text-emerald-100 text-xs bg-white/10 px-2 py-1 rounded-full">
              1 EUR = {currency.symbol}{currency.rate} {currency.code}
            </span>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex border-b border-border overflow-x-auto bg-background">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors ${
              activeSection === s.id
                ? 'border-b-2 border-emerald-600 text-emerald-700 bg-emerald-50' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={s.icon} size={16} />
            {s.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* Tuition Section */}
        {activeSection === 'tuition' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Program Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {programTypes.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setSelectedProgram(i);
                      setTuitionAmount(Math.round((p.minTuition + p.maxTuition) / 2));
                    }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      selectedProgram === i
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-800' :'border-border hover:border-emerald-300 text-foreground'
                    }`}
                  >
                    <div className="font-medium text-sm">{p.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {fmt(p.minTuition)} – {fmt(p.maxTuition)}/yr
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Field of Study</label>
              <select
                value={selectedField}
                onChange={e => setSelectedField(Number(e.target.value))}
                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {fieldOfStudy.map((f, i) => (
                  <option key={i} value={i}>{f.label}</option>
                ))}
              </select>
              {field.multiplier !== 1 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {field.multiplier > 1 ? `+${Math.round((field.multiplier - 1) * 100)}%` : `-${Math.round((1 - field.multiplier) * 100)}%`} adjustment for this field
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Annual Tuition Fee (EUR): <span className="text-emerald-600">€{tuitionAmount.toLocaleString()}</span>
              </label>
              <input
                type="range"
                min={program.minTuition}
                max={program.maxTuition}
                step={500}
                value={tuitionAmount}
                onChange={e => setTuitionAmount(Number(e.target.value))}
                className="w-full accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{fmt(program.minTuition)}</span>
                <span>{fmt(program.maxTuition)}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Study Duration: <span className="text-emerald-600">{duration} months</span>
              </label>
              <input
                type="range"
                min={6}
                max={48}
                step={6}
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                className="w-full accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>6 months</span>
                <span>48 months (4 years)</span>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-emerald-700 font-medium">Adjusted Annual Tuition</p>
                  <p className="text-xs text-emerald-600">After field of study adjustment</p>
                </div>
                <p className="text-2xl font-bold text-emerald-700">{fmt(adjustedTuition)}/yr</p>
              </div>
              <div className="mt-3 pt-3 border-t border-emerald-200 flex justify-between items-center">
                <p className="text-sm text-emerald-700 font-semibold">Total Tuition ({duration} months)</p>
                <p className="text-xl font-bold text-emerald-800">{fmt(totalTuition)}</p>
              </div>
            </div>

            <button
              onClick={() => setActiveSection('living')}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
            >
              Next: Living Costs →
            </button>
          </div>
        )}

        {/* Living Costs Section */}
        {activeSection === 'living' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Accommodation Type</label>
              <div className="space-y-2">
                {accommodationOptions.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedAccommodation(i)}
                    className={`w-full flex justify-between items-center p-3 rounded-lg border-2 transition-all ${
                      selectedAccommodation === i
                        ? 'border-emerald-600 bg-emerald-50' :'border-border hover:border-emerald-300'
                    }`}
                  >
                    <span className={`text-sm font-medium ${selectedAccommodation === i ? 'text-emerald-800' : 'text-foreground'}`}>{opt.label}</span>
                    <span className={`text-sm font-bold ${selectedAccommodation === i ? 'text-emerald-700' : 'text-muted-foreground'}`}>{fmt(opt.cost)}/mo</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Food & Groceries: <span className="text-emerald-600">{fmt(foodBudget)}/month</span>
                </label>
                <input type="range" min={200} max={700} step={25} value={foodBudget} onChange={e => setFoodBudget(Number(e.target.value))} className="w-full accent-emerald-600" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1"><span>{fmt(200)}</span><span>{fmt(700)}</span></div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Transportation: <span className="text-emerald-600">{fmt(transportBudget)}/month</span>
                </label>
                <input type="range" min={50} max={300} step={10} value={transportBudget} onChange={e => setTransportBudget(Number(e.target.value))} className="w-full accent-emerald-600" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1"><span>{fmt(50)}</span><span>{fmt(300)}</span></div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Entertainment & Misc: <span className="text-emerald-600">{fmt(entertainmentBudget)}/month</span>
                </label>
                <input type="range" min={50} max={500} step={25} value={entertainmentBudget} onChange={e => setEntertainmentBudget(Number(e.target.value))} className="w-full accent-emerald-600" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1"><span>{fmt(50)}</span><span>{fmt(500)}</span></div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
              <h4 className="font-semibold text-blue-800 text-sm mb-3">Monthly Living Breakdown</h4>
              {[
                { label: 'Accommodation', value: accommodationOptions[selectedAccommodation].cost },
                { label: 'Food & Groceries', value: foodBudget },
                { label: 'Transportation', value: transportBudget },
                { label: 'Utilities', value: 80 },
                { label: 'Entertainment & Misc', value: entertainmentBudget },
              ].map(item => (
                <div key={item.label} className="flex justify-between text-sm">
                  <span className="text-blue-700">{item.label}</span>
                  <span className="font-semibold text-blue-800">{fmt(item.value)}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-blue-200 flex justify-between">
                <span className="font-bold text-blue-800">Monthly Total</span>
                <span className="font-bold text-blue-900 text-lg">{fmt(monthlyLiving)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-bold text-blue-800">Total Living ({duration} months)</span>
                <span className="font-bold text-blue-900 text-lg">{fmt(totalLiving)}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setActiveSection('tuition')} className="flex-1 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors">← Back</button>
              <button onClick={() => setActiveSection('scholarships')} className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors">Next: Scholarships →</button>
            </div>
          </div>
        )}

        {/* Scholarships Section */}
        {activeSection === 'scholarships' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Select scholarships you may be eligible for to see your net cost:</p>
            {scholarships.map((s, i) => (
              <div
                key={i}
                onClick={() => toggleScholarship(i)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedScholarships.includes(i)
                    ? 'border-emerald-600 bg-emerald-50' :'border-border hover:border-emerald-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedScholarships.includes(i) ? 'border-emerald-600 bg-emerald-600' : 'border-border'
                      }`}>
                        {selectedScholarships.includes(i) && (
                          <Icon name="CheckIcon" size={12} className="text-white" />
                        )}
                      </div>
                      <h4 className={`font-semibold text-sm ${selectedScholarships.includes(i) ? 'text-emerald-800' : 'text-foreground'}`}>{s.name}</h4>
                    </div>
                    <p className="text-xs text-muted-foreground ml-7">{s.eligibility}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold text-lg ${selectedScholarships.includes(i) ? 'text-emerald-700' : 'text-foreground'}`}>{fmt(s.amount)}</p>
                    <p className="text-xs text-muted-foreground">€{s.amount.toLocaleString()} EUR</p>
                  </div>
                </div>
              </div>
            ))}

            {selectedScholarships.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-emerald-800">Total Scholarship Amount</span>
                  <span className="text-2xl font-bold text-emerald-700">{fmt(totalScholarshipAmount)}</span>
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button onClick={() => setActiveSection('living')} className="flex-1 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors">← Back</button>
              <button onClick={() => setActiveSection('summary')} className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors">View Summary →</button>
            </div>
          </div>
        )}

        {/* Summary Section */}
        {activeSection === 'summary' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-xs text-blue-600 font-medium mb-1">Total Tuition</p>
                <p className="text-xl font-bold text-blue-800">{fmt(totalTuition)}</p>
                <p className="text-xs text-blue-500 mt-1">{duration} months</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <p className="text-xs text-purple-600 font-medium mb-1">Total Living Costs</p>
                <p className="text-xl font-bold text-purple-800">{fmt(totalLiving)}</p>
                <p className="text-xs text-purple-500 mt-1">{fmt(monthlyLiving)}/month</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-xs text-emerald-600 font-medium mb-1">Scholarships</p>
                <p className="text-xl font-bold text-emerald-800">-{fmt(totalScholarshipAmount)}</p>
                <p className="text-xs text-emerald-500 mt-1">{selectedScholarships.length} applied</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 rounded-xl p-5 text-white">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-emerald-100 text-sm">Gross Total Cost</p>
                  <p className="text-2xl font-bold line-through opacity-70">{fmt(grossTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-100 text-sm">Net Estimated Cost</p>
                  <p className="text-3xl font-bold">{fmt(netTotal)}</p>
                </div>
              </div>
              <div className="border-t border-white/20 pt-3 text-xs text-emerald-100 space-y-1">
                <div className="flex justify-between">
                  <span>Program: {programTypes[selectedProgram].label}</span>
                  <span>Field: {fieldOfStudy[selectedField].label}</span>
                </div>
                <div className="flex justify-between">
                  <span>Duration: {duration} months</span>
                  <span>Currency: {currency.code}</span>
                </div>
              </div>
            </div>

            {/* Cost Breakdown Bar */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Cost Distribution</h4>
              <div className="space-y-2">
                {[
                  { label: 'Tuition', value: totalTuition, color: 'bg-blue-500' },
                  { label: 'Accommodation', value: accommodationOptions[selectedAccommodation].cost * duration, color: 'bg-purple-500' },
                  { label: 'Food', value: foodBudget * duration, color: 'bg-orange-400' },
                  { label: 'Transport', value: transportBudget * duration, color: 'bg-yellow-400' },
                  { label: 'Other', value: (entertainmentBudget + 80) * duration, color: 'bg-pink-400' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-24 flex-shrink-0">{item.label}</span>
                    <div className="flex-1 bg-muted rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${item.color}`}
                        style={{ width: `${Math.min(100, (item.value / grossTotal) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-foreground w-24 text-right">{fmt(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-2">
                <Icon name="InformationCircleIcon" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  These are estimates based on average costs. Actual costs may vary by university, city (Dublin vs. other cities), and lifestyle. Exchange rates are approximate and subject to change.
                </p>
              </div>
            </div>

            <button
              onClick={() => setActiveSection('tuition')}
              className="w-full py-3 border border-emerald-600 text-emerald-700 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
            >
              ↺ Recalculate
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
